// Guided Sandbox — React port of kula-create's guided-sandbox.html.
//
// Pedagogy: open practice with dimensional heuristic scoring. Learner writes a
// free-text response to an authentic task; per-dimension keyword-based scoring
// reveals which aspects landed and which didn't.
//
// Single-row capture on submit:
//   { submission: string, scores: {[dimensionId]: number} }.

import { useState, useCallback, useMemo, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { GuidedSandboxSlideData, Theme } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { feedbackStyle } from '../../shared/SlideShared';
import { Reveal } from '../../shared/Reveal';
import type { ActivityResponsePayload } from '../../../../../types';

interface Props {
  data: GuidedSandboxSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: GuidedSandboxSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

export function GuidedSandboxSlide({ data, theme, isEditing, onUpdate, onActivityResponse, onComplete, savedResponse }: Props) {
  if (isEditing) return <BasicEditor data={data} theme={theme} onUpdate={onUpdate} />;
  return (
    <Student
      data={data}
      theme={theme}
      onActivityResponse={onActivityResponse}
      onComplete={onComplete}
      savedResponse={savedResponse}
    />
  );
}

/**
 * Per-dimension score: keyword-based heuristic. Counts how many of the
 * dimension's keywords appear as whole words/phrases (case-insensitive,
 * word-boundary matched — "art" must not match "particular") in the
 * submission; scaled to [0, 100]. The renderer is the scoring authority —
 * the LMS doesn't grade this further.
 */
function scoreDimension(
  submission: string,
  dim: GuidedSandboxSlideData['dimensions'][number],
): number {
  if (dim.keywords.length === 0) return 0;
  const hits = dim.keywords.filter((kw) => {
    const escaped = kw.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (!escaped) return false;
    return new RegExp(`\\b${escaped}\\b`, 'i').test(submission);
  }).length;
  return Math.min(100, Math.round((hits / dim.keywords.length) * 100));
}

// Substance gate. The scorer is a build-time keyword heuristic with no runtime
// AI, so a near-empty submission ("Sorry to hear about that.") can produce a
// confident, nonsensical score (Overall 6 / Acknowledgement 17 / Ownership 0).
// Require a real attempt before any number is shown, and never present the
// number as authoritative. (Review doc §9; interactionRegistry guided-sandbox
// qaRejectRules: "Scorer can emit a confident score on near-empty input".)
const MIN_WORDS = 20;

function Student({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: GuidedSandboxSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  const correct = feedbackStyle(theme, 'correct');
  const warning = feedbackStyle(theme, 'warning');
  const incorrect = feedbackStyle(theme, 'incorrect');
  // A saved {submission, scores, overallScore} payload is the end state —
  // restore the submitted view (scores recompute deterministically).
  const savedSubmission = typeof savedResponse?.submission === 'string' ? savedResponse.submission : null;
  const [submission, setSubmission] = useState(savedSubmission ?? '');
  const [submitted, setSubmitted] = useState(savedSubmission !== null);
  const [submitCount, setSubmitCount] = useState(savedSubmission !== null ? 1 : 0);
  const [finished, setFinished] = useState(false);
  const completedRef = useRef(savedSubmission !== null);

  const wordCount = useMemo(
    () => (submission.trim() ? submission.trim().split(/\s+/).length : 0),
    [submission],
  );
  const meetsBar = wordCount >= MIN_WORDS;

  // Dimensions with no keywords (or an authored weight of 0) can't be
  // scored — exclude them from the weighted average instead of pinning 0.
  const scorableDims = useMemo(
    () => data.dimensions.filter((d) => d.keywords.length > 0 && (d.weight ?? 1) > 0),
    [data.dimensions],
  );
  const isScored = scorableDims.length > 0;

  const scores = useMemo(() => {
    const obj: Record<string, number> = {};
    for (const dim of data.dimensions) {
      if (dim.keywords.length > 0) obj[dim.id] = scoreDimension(submission, dim);
    }
    return obj;
  }, [submission, data.dimensions]);

  // null = unscored (no scorable dimensions) — submit then counts as completion.
  const overallScore = useMemo(() => {
    const totalWeight = scorableDims.reduce((s, d) => s + (d.weight ?? 1), 0);
    if (totalWeight === 0) return null;
    const weighted = scorableDims.reduce((s, d) => s + (scores[d.id] ?? 0) * (d.weight ?? 1), 0);
    return Math.round(weighted / totalWeight);
  }, [scores, scorableDims]);

  const markComplete = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete?.();
  }, [onComplete]);

  const submit = useCallback(() => {
    setSubmitted(true);
    setSubmitCount((n) => n + 1);
    onActivityResponse?.({
      activityType: 'guided-sandbox',
      mode: 'survey',
      responseData: overallScore === null ? { submission, scores } : { submission, scores, overallScore },
    });
    if (overallScore === null || overallScore >= data.minScore) markComplete();
  }, [submission, scores, overallScore, data.minScore, onActivityResponse, markComplete]);

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[760px] px-6 py-10">
        <header className="text-center mb-6">
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginBottom: 8 }}>
            {data.title}
          </h1>
          {data.subtitle && <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>}
        </header>

        {/* Task */}
        <div className="rounded-lg p-5 mb-4 border-l-4" style={{ background: theme.colors.surface, borderColor: accent }}>
          <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>Task</p>
          <p className="leading-relaxed text-sm" style={{ color: theme.colors.text }}>{data.task}</p>
        </div>

        {/* Submission */}
        <textarea
          value={submission}
          onChange={(e) => setSubmission(e.target.value)}
          disabled={submitted}
          placeholder={data.placeholder}
          rows={10}
          className="w-full px-3 py-2 rounded border text-sm mb-3"
          style={{
            background: theme.colors.surface,
            borderColor: `${theme.colors.textMuted}33`,
            color: theme.colors.text,
            fontFamily: theme.fonts.body,
          }}
        />

        {!submitted && (
          <div className="mb-4">
            <button
              onClick={submit}
              disabled={!meetsBar}
              className="w-full px-6 py-3 rounded-lg font-semibold text-sm"
              style={{
                background: accent,
                color: '#fff',
                cursor: meetsBar ? 'pointer' : 'not-allowed',
                opacity: meetsBar ? 1 : 0.4,
              }}
            >
              {submitCount > 0 ? 'Resubmit my work' : 'Submit my work'}
            </button>
            {!meetsBar && (
              <p className="text-xs mt-2 text-center leading-snug" style={{ color: theme.colors.textMuted }}>
                Write at least {MIN_WORDS} words to get feedback. You{'’'}re at {wordCount}.
                This activity gives keyword-based guidance, so it needs a real attempt to be useful.
              </p>
            )}
          </div>
        )}

        <AnimatePresence>
          {submitted && (
            <Reveal variant="up" className="space-y-3">
              {/* Payoff register: heavier accent rule + larger radius than setup cards */}
              {isScored && overallScore !== null ? (
                <div
                  className="rounded-xl border-l-[5px] p-4 text-center shadow-sm"
                  style={{
                    background: overallScore >= data.minScore ? correct.bg : `${accent}15`,
                    borderColor: overallScore >= data.minScore ? correct.border : accent,
                  }}
                >
                  <p className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: overallScore >= data.minScore ? correct.text : accent }}>
                    Overall
                  </p>
                  <p className="text-3xl font-bold" style={{ color: overallScore >= data.minScore ? correct.text : accent }}>
                    {overallScore}
                  </p>
                  <p className="text-xs mt-1" style={{ color: theme.colors.textMuted }}>
                    {overallScore >= data.minScore ? `Above minimum (${data.minScore})` : `Below minimum (${data.minScore})`}
                  </p>
                  <p className="text-[11px] mt-2 leading-snug" style={{ color: theme.colors.textMuted }}>
                    Indicative guide only, matched against keywords, not full comprehension.
                    Use the tips below to judge your own work.
                  </p>
                </div>
              ) : (
                <div className="rounded-xl border-l-[5px] p-4 text-center shadow-sm" style={{ background: correct.bg, borderColor: correct.border }}>
                  <p className="text-sm font-semibold" style={{ color: correct.text }}>Work submitted</p>
                  <p className="text-xs mt-1" style={{ color: theme.colors.textMuted }}>
                    This activity isn't auto-scored — your response has been recorded.
                  </p>
                </div>
              )}

              {data.dimensions.map((dim, dIdx) => {
                const score = dim.keywords.length > 0 ? scores[dim.id] ?? 0 : null;
                const tier = score === null ? warning : score >= 50 ? correct : score >= 25 ? warning : incorrect;
                const guidance = data.guidance?.[dim.id] ?? '';
                return (
                  <Reveal
                    key={dim.id}
                    variant="up"
                    delay={0.06 * (dIdx + 1)}
                    className="rounded-xl p-4 border shadow-sm"
                    style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}33` }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm" style={{ color: theme.colors.text }}>{dim.label}</h4>
                      {score !== null && (
                        <span className="text-sm font-bold" style={{ color: tier.text }}>
                          {score}
                        </span>
                      )}
                    </div>
                    <p className="text-xs mb-2" style={{ color: theme.colors.textMuted }}>{dim.description}</p>
                    <p className="text-xs" style={{ color: theme.colors.text }}>
                      <strong>Tips:</strong> {dim.tips}
                    </p>
                    {guidance && (
                      <p className="text-xs mt-2 italic" style={{ color: theme.colors.textMuted }}>{guidance}</p>
                    )}
                  </Reveal>
                );
              })}

              {/* Revise/resubmit loop — re-enable the textarea; each resubmit fires onActivityResponse. */}
              {!finished && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setSubmitted(false)}
                    className="flex-1 px-6 py-3 rounded-lg font-semibold text-sm border-2"
                    style={{ background: 'transparent', borderColor: accent, color: accent, cursor: 'pointer' }}
                  >
                    Revise my work
                  </button>
                  {isScored && overallScore !== null && overallScore < data.minScore && submitCount >= 2 && (
                    <button
                      onClick={() => {
                        setFinished(true);
                        markComplete();
                      }}
                      className="flex-1 px-6 py-3 rounded-lg font-semibold text-sm"
                      style={{ background: `${theme.colors.textMuted}22`, color: theme.colors.text, cursor: 'pointer' }}
                    >
                      Finish below minimum
                    </button>
                  )}
                </div>
              )}
              {finished && (
                <p className="text-xs text-center" style={{ color: theme.colors.textMuted }}>
                  Finished below the minimum score — your latest submission is recorded.
                </p>
              )}
            </Reveal>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function BasicEditor({
  data,
  theme,
  onUpdate,
}: {
  data: GuidedSandboxSlideData;
  theme: Theme;
  onUpdate: (data: GuidedSandboxSlideData) => void;
}) {
  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[720px] space-y-3">
        <input
          value={data.title}
          onChange={(e) => onUpdate({ ...data, title: e.target.value })}
          placeholder="Title…"
          className="w-full bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => onUpdate({ ...data, subtitle: e.target.value })}
          placeholder="Subtitle"
          className="w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
          style={{ color: theme.colors.textMuted }}
        />
        <textarea
          value={data.task}
          onChange={(e) => onUpdate({ ...data, task: e.target.value })}
          placeholder="Task — what the learner is producing"
          rows={3}
          className="w-full px-3 py-2 rounded border text-sm"
          style={{
            background: theme.colors.surface,
            borderColor: `${theme.colors.textMuted}33`,
            color: theme.colors.text,
          }}
        />
        <input
          value={data.placeholder}
          onChange={(e) => onUpdate({ ...data, placeholder: e.target.value })}
          placeholder="Textarea placeholder"
          className="w-full px-3 py-2 rounded border text-sm"
          style={{
            background: theme.colors.surface,
            borderColor: `${theme.colors.textMuted}33`,
            color: theme.colors.text,
          }}
        />
        <label className="block text-sm">
          Min score (passing threshold):{' '}
          <input
            type="number"
            value={data.minScore}
            onChange={(e) => onUpdate({ ...data, minScore: Number(e.target.value) })}
            className="px-2 py-1 rounded border text-sm w-20"
            style={{
              background: theme.colors.surface,
              borderColor: `${theme.colors.textMuted}33`,
              color: theme.colors.text,
            }}
          />
        </label>
        <div className="rounded p-3 text-xs" style={{ background: `${theme.colors.textMuted}11`, color: theme.colors.textMuted }}>
          {data.dimensions.length} dimension{data.dimensions.length === 1 ? '' : 's'}.
          Structural edits (dimensions, keywords) are AI-only. Regenerate via AI build.
        </div>
      </div>
    </div>
  );
}
